import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Upload, Video, Monitor, Loader2, CheckCircle2, ShieldAlert, Link as LinkIcon, Sparkles, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import MilestoneCelebration from '../components/MilestoneCelebration';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UploadMilestoneVideo() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState('landing');
  const [uploadProgress, setUploadProgress] = useState(0);
  // Landing video can be uploaded as a file OR linked by URL (YouTube/Vimeo/mp4)
  const [inputMode, setInputMode] = useState('file'); // 'file' | 'url'
  const [videoUrl, setVideoUrl] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [savingUrl, setSavingUrl] = useState(false);
  const [showMilestonePreview, setShowMilestonePreview] = useState(false);
  const { user, loading: authLoading, getAuthHeader } = useAuth();
  const navigate = useNavigate();

  // Auth guard — runs once auth has resolved (no flash redirects on slow networks).
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      toast.error('Please log in to upload videos');
      navigate('/login', { replace: true });
      return;
    }
    if (!user.is_admin) {
      toast.error('Admin access required for video uploads');
      navigate('/dashboard', { replace: true });
    }
  }, [user, authLoading, navigate]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.toLowerCase().endsWith('.mp4')) {
        toast.error('Only MP4 files are supported');
        return;
      }
      const maxSize = uploadType === 'landing' ? 500 : 4 * 1024;
      if (selectedFile.size > maxSize * 1024 * 1024) {
        toast.error(`File size must be less than ${maxSize >= 1024 ? (maxSize / 1024) + 'GB' : maxSize + 'MB'}`);
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please select a video file');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    let uploadId = null;
    try {
      // 1) Init session — server returns chunk_size + total_chunks.
      const initRes = await axios.post(
        `${API_URL}/api/admin/videos/upload/init`,
        { kind: uploadType, filename: file.name, total_size: file.size },
        { headers: getAuthHeader() }
      );
      uploadId = initRes.data.upload_id;
      const chunkSize = initRes.data.chunk_size;
      const totalChunks = initRes.data.total_chunks;

      // 2) Stream chunks sequentially. Each chunk is small enough to bypass the 100MB ingress cap.
      let bytesSent = 0;
      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const blob = file.slice(start, end);
        const fd = new FormData();
        fd.append('chunk', blob, `${i}.bin`);

        await axios.post(
          `${API_URL}/api/admin/videos/upload/chunk/${uploadId}/${i}`,
          fd,
          {
            headers: { ...getAuthHeader() },
            timeout: 0,
            maxContentLength: Infinity,
            maxBodyLength: Infinity,
            onUploadProgress: (pe) => {
              if (pe.total) {
                const chunkBytes = pe.loaded;
                const totalLoaded = bytesSent + chunkBytes;
                const pct = Math.min(99, Math.round((totalLoaded * 100) / file.size));
                setUploadProgress(pct);
              }
            },
          }
        );
        bytesSent += (end - start);
        setUploadProgress(Math.min(99, Math.round((bytesSent * 100) / file.size)));
      }

      // 3) Finalize — server concatenates chunks, validates size, creates DB record.
      await axios.post(
        `${API_URL}/api/admin/videos/upload/finalize/${uploadId}`,
        {},
        { headers: getAuthHeader(), timeout: 0 }
      );
      setUploadProgress(100);

      toast.success(`${uploadType === 'landing' ? 'Landing page' : 'Milestone'} video uploaded successfully!`);
      navigate('/admin');
    } catch (error) {
      console.error('Upload failed:', error);
      // Best-effort abort — frees up the chunk dir on the server.
      if (uploadId) {
        try {
          await axios.post(
            `${API_URL}/api/admin/videos/upload/abort/${uploadId}`,
            {},
            { headers: getAuthHeader() }
          );
        } catch (_) { /* ignore */ }
      }
      const status = error.response?.status;
      const detail = error.response?.data?.detail || error.message || 'Failed to upload video';

      if (status === 401 || status === 403) {
        toast.error('Your session expired. Please log in again to retry the upload.');
        return;
      }
      if (error.code === 'ECONNABORTED' || error.message === 'Network Error') {
        toast.error('Upload connection dropped. Try a smaller file or a faster network.');
        return;
      }
      toast.error(typeof detail === 'string' ? detail : 'Failed to upload video');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSaveUrl = async (e) => {
    e.preventDefault();
    if (!videoUrl.trim()) {
      toast.error('Paste a YouTube, Vimeo or direct MP4 URL');
      return;
    }
    setSavingUrl(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/videos/landing/url`,
        { url: videoUrl.trim(), title: videoTitle.trim() || 'Featured Masterclass' },
        { headers: getAuthHeader() }
      );
      toast.success('Landing video linked successfully');
      navigate('/admin');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save URL');
    } finally {
      setSavingUrl(false);
    }
  };

  // Loading state — wait for auth to resolve so we don't briefly show the
  // form to a user whose session is still being verified.
  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground text-sm uppercase tracking-widest">
            Verifying access…
          </p>
        </div>
      </div>
    );
  }

  if (!user.is_admin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="wm-card p-8 max-w-md text-center">
          <ShieldAlert className="w-10 h-10 mx-auto mb-4 text-rose-500" />
          <h2 className="text-2xl font-playfair font-bold mb-2">Admin only</h2>
          <p className="text-muted-foreground text-sm">
            Video uploads are restricted to admin accounts.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      {showMilestonePreview && (
        <MilestoneCelebration
          forceOpen={true}
          onClose={() => setShowMilestonePreview(false)}
        />
      )}
      <div className="max-w-2xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin')}
          className="mb-8 text-foreground hover:text-primary"
          data-testid="back-btn"
        >
          <ArrowLeft className="mr-2 w-4 h-4" /> Back to Admin Panel
        </Button>

        <div className="wm-card p-8">
          <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
            <h1 className="text-3xl font-playfair font-bold text-foreground">Upload Video</h1>
            {uploadType === 'milestone' && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowMilestonePreview(true)}
                className="border-gold/50 text-gold hover:bg-gold/10"
                data-testid="preview-milestone-btn"
              >
                <PartyPopper className="w-4 h-4 mr-2" /> Preview celebration
              </Button>
            )}
          </div>
          <p className="text-muted-foreground mb-8">Choose a video type and upload an MP4 file or link an external video URL.</p>

          {/* Video Type Selector */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            <button
              onClick={() => { setUploadType('landing'); setFile(null); }}
              className={`p-4 rounded-lg border-2 transition text-left ${
                uploadType === 'landing'
                  ? 'border-primary bg-primary/5'
                  : 'border-white/10 bg-white/[0.02] hover:border-white/20'
              }`}
              data-testid="type-landing-btn"
            >
              <Monitor className={`w-6 h-6 mb-2 ${uploadType === 'landing' ? 'text-primary' : 'text-muted-foreground'}`} />
              <p className="font-semibold text-foreground">Landing Page Video</p>
              <p className="text-xs text-muted-foreground mt-1">Upload an MP4 or link a YouTube/Vimeo URL.</p>
            </button>
            <button
              onClick={() => { setUploadType('milestone'); setFile(null); setInputMode('file'); }}
              className={`p-4 rounded-lg border-2 transition text-left ${
                uploadType === 'milestone'
                  ? 'border-primary bg-primary/5'
                  : 'border-white/10 bg-white/[0.02] hover:border-white/20'
              }`}
              data-testid="type-milestone-btn"
            >
              <Video className={`w-6 h-6 mb-2 ${uploadType === 'milestone' ? 'text-primary' : 'text-muted-foreground'}`} />
              <p className="font-semibold text-foreground">$1M Milestone Video</p>
              <p className="text-xs text-muted-foreground mt-1">Celebration video when pot reaches $1M. Max 4GB.</p>
            </button>
          </div>

          {/* File-vs-URL toggle — only shown for the landing-page slot */}
          {uploadType === 'landing' && (
            <div className="inline-flex rounded-sm border border-border overflow-hidden mb-6" role="tablist" aria-label="Input method">
              <button
                type="button"
                onClick={() => setInputMode('file')}
                className={`px-4 py-2 text-xs uppercase tracking-widest transition-colors ${
                  inputMode === 'file' ? 'bg-gold text-black font-bold' : 'bg-transparent text-muted-foreground hover:text-foreground'
                }`}
                data-testid="mode-file-btn"
              >
                <Upload className="w-3.5 h-3.5 mr-1.5 inline" /> Upload file
              </button>
              <button
                type="button"
                onClick={() => setInputMode('url')}
                className={`px-4 py-2 text-xs uppercase tracking-widest transition-colors border-l border-border ${
                  inputMode === 'url' ? 'bg-gold text-black font-bold' : 'bg-transparent text-muted-foreground hover:text-foreground'
                }`}
                data-testid="mode-url-btn"
              >
                <LinkIcon className="w-3.5 h-3.5 mr-1.5 inline" /> Link URL
              </button>
            </div>
          )}

          {uploadType === 'landing' && inputMode === 'url' ? (
            <form onSubmit={handleSaveUrl} className="space-y-6">
              <div>
                <Label htmlFor="title" className="wm-label">Title (optional)</Label>
                <Input
                  id="title"
                  type="text"
                  value={videoTitle}
                  onChange={(e) => setVideoTitle(e.target.value)}
                  placeholder="Featured Masterclass"
                  className="mt-2"
                  data-testid="video-title-input"
                />
              </div>
              <div>
                <Label htmlFor="video_url" className="wm-label">Masterclass URL</Label>
                <Input
                  id="video_url"
                  type="url"
                  required
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
                  className="mt-2"
                  data-testid="video-url-input"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Supports YouTube (full or short links), Vimeo, and direct .mp4 URLs. We'll auto-generate the embeddable version.
                </p>
              </div>
              <Button
                type="submit"
                disabled={savingUrl || !videoUrl.trim()}
                className="w-full h-12 bg-gold-gradient text-black hover:shadow-gold-glow font-semibold uppercase tracking-wider"
                data-testid="save-url-btn"
              >
                {savingUrl ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" /> Link masterclass URL</>
                )}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleUpload} className="space-y-6">
              <div>
                <Label htmlFor="video" className="wm-label">
                  Video File (MP4)
                </Label>
                <Input
                  id="video"
                  type="file"
                  accept=".mp4"
                  onChange={handleFileChange}
                  className="mt-2 bg-[#0a0a12] border-white/10 text-foreground"
                  data-testid="video-file-input"
                />
                {file && (
                  <p className="text-sm text-emerald-400 mt-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    {file.name} ({(file.size / (1024 * 1024)).toFixed(1)} MB)
                  </p>
                )}
              </div>

              {/* Upload Progress */}
              {uploading && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Uploading...</span>
                    <span className="text-primary font-medium">{uploadProgress}%</span>
                  </div>
                  <div className="h-2 bg-[#0a0a12] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gold-gradient rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="bg-[#0a0a12] border border-white/10 rounded-lg p-4">
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Note:</strong>{' '}
                  {uploadType === 'landing'
                    ? 'This video will appear in the "Featured Masterclass" section on the landing page.'
                    : 'This video will auto-play in a celebration modal when the Millionaire Pot reaches $1,000,000. Click "Preview celebration" above to test it.'}
                </p>
              </div>

              <Button
                type="submit"
                disabled={!file || uploading}
                className="w-full h-12 bg-gold-gradient text-black hover:shadow-gold-glow font-semibold uppercase tracking-wider"
                data-testid="upload-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading {uploadProgress}%...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload {uploadType === 'landing' ? 'Landing Page' : 'Milestone'} Video
                  </>
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
